import { inject, Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { finalize, map, Observable, of } from 'rxjs';

import { AiSearchDto } from '../../../../shared/models';
import {
  ConfirmModalComponent,
  ConfirmModalData,
} from '../../../system-shared/components/confirm-modal/confirm-modal.component';
import { AiSearchStateService } from './ai-search-state.service';

const OWNERSHIP_CONFIRM_TITLE_KEY = 'AI_SEARCH.OWNERSHIP_CONFIRM.TITLE';
const OWNERSHIP_CONFIRM_TEXT_KEY = 'AI_SEARCH.OWNERSHIP_CONFIRM.TEXT';

@Injectable()
export class AiSearchEditGuardService {
  private readonly stateService = inject(AiSearchStateService);
  private readonly dialog = inject(MatDialog);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  private confirmDialogRef: MatDialogRef<ConfirmModalComponent, boolean> | null = null;

  /**
   * Returns Observable<true> if the current user owns the AI search OR confirmed the
   * "edit will fork" prompt. Returns Observable<false> if the user cancelled or if
   * a confirm dialog is already open (prevents stacking when the user fires multiple
   * edit actions in quick succession).
   */
  confirmEditIfNotOwner(): Observable<boolean> {
    if (this.stateService.getIsOwner()) {
      return of(true);
    }

    if (this.confirmDialogRef) {
      return of(false);
    }

    const data: ConfirmModalData = {
      titleKey: OWNERSHIP_CONFIRM_TITLE_KEY,
      textKey: OWNERSHIP_CONFIRM_TEXT_KEY,
    };

    this.confirmDialogRef = this.dialog.open<ConfirmModalComponent, ConfirmModalData, boolean>(
      ConfirmModalComponent,
      { data },
    );

    return this.confirmDialogRef.afterClosed().pipe(
      finalize(() => {
        this.confirmDialogRef = null;
      }),
      map((v) => v === true),
    );
  }

  /**
   * Apply an edit-endpoint response with a full state replace and navigate to
   * the new aiSearchId when the backend forked the search. Use for endpoints
   * that always return the canonical full DTO (form submit, accuracy change).
   */
  handleEditResponse(dto: AiSearchDto | null): void {
    if (!dto) {
      return;
    }

    const forkedId = this.stateService.applyEditResponse(dto);

    if (forkedId) {
      this.navigateToForkedSearch(forkedId);
    }
  }

  /**
   * Like {@link handleEditResponse}, but only acts when the backend forked the
   * search. Use for endpoints whose owner-path response is consumed via a
   * partial state patch (adjustments) — calling the full apply would reset the
   * form fields the partial patch was designed to preserve.
   */
  handleEditResponseIfForked(dto: AiSearchDto | null): void {
    if (!dto?.id) {
      return;
    }

    const currentId = this.stateService.getAiSearchId();

    if (dto.id === currentId) {
      return;
    }

    this.stateService.applyEditResponse(dto);
    this.navigateToForkedSearch(dto.id);
  }

  private navigateToForkedSearch(newAiSearchId: string): void {
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { aiSearchId: newAiSearchId },
      queryParamsHandling: 'merge',
      replaceUrl: false,
    });
  }
}
